/**
 * Implements a paginated view for content
 * @author Peter Bosch <me@pbos.ch>
 */
export class GVPager {
  /**
   * Creates a paginated view
   * @param {_jQuery} element The element to insert our HTML into.
   * @param {function} contentRender The formatting callback: {array} => {html string}
   * @param {number} itemsPerPage The number of result items to show in one page
   * @param {number} maxPagerButtons The number of page buttons to show at most.
   */
  constructor(element, contentRender, itemsPerPage = 10, maxPagerButtons = 5) {
    /**
     * The formatting callback: {array} => {html string}
     * @type {function}
     */
    this.contentRenderFunc = contentRender;

    /**
     * The number of result items to show in one page
     * @type {number}
     */
    this.itemsPerPage = itemsPerPage;

    /**
     * The number of page buttons to show at most.
     * @type {number}
     */
    this.maxPagerButtons = maxPagerButtons;

    /**
     * The index of the first item currently shown.
     * @type {number}
     */
    this.itemPosition = 0;

    /**
     * The template function.
     */
    this.pagerTemplate = require("./views/pager.handlebars");

    /**
     * The element to insert our HTML into.
     * @type {_jQuery}
     */
    this.element = element;

    this.onRender = () => 0;
  }

  /**
   * Jumps to a specific page and refreshes the pager view
   * @param page
   */
  goto(page) {
    this.itemPosition = this.itemsPerPage * +page;
    if (this.itemPosition >= this.content.length)
      this.itemPosition = this.content.length - this.itemsPerPage;
    if (this.itemPosition < 0) this.itemPosition = 0;
    this.render();
  }

  /**
   * Update the content of the view
   * @param content
   */
  update(content) {
    this.content = content;
    this.pageCount = Math.ceil(this.content.length / this.itemsPerPage);
    this.render();
  }

  /**
   * Generate the button list
   * @private
   * @returns {Array} the array that goes into the template.
   */
  generateButtonList() {
    const currentPage = Math.floor(this.itemPosition / this.itemsPerPage);

    let buttonCount = this.pageCount;

    let buttons = [];

    buttons.push({
      id: "prev",
      caption: "Vorige",
      active: false,
      disabled: currentPage === 0,
    });

    if (buttonCount + 2 > this.maxPagerButtons) {
      buttonCount = this.maxPagerButtons - 2;
    }

    buttons.push({
      id: "0",
      caption: "1",
      active: 0 === currentPage,
      disabled: false,
    });

    var startButton = currentPage - Math.floor(buttonCount / 2);
    if (startButton <= 1) {
      startButton = 1;
    } else {
      buttons.push({
        id: "leftell",
        caption: "⋯",
        active: false,
        disabled: true,
      });
    }

    var endButton = startButton + buttonCount;

    if (endButton > this.pageCount - 1) {
      endButton = this.pageCount - 1;
    }

    for (let p = startButton; p < endButton; p++) {
      buttons.push({
        id: p,
        caption: p + 1,
        active: p === currentPage,
        disabled: false,
      });
    }

    if (endButton < this.pageCount - 1) {
      buttons.push({
        id: "rightell",
        caption: "⋯",
        active: false,
        disabled: true,
      });
    }

    if (this.pageCount > 1) {
      buttons.push({
        id: this.pageCount - 1,
        caption: this.pageCount,
        active: currentPage === this.pageCount - 1,
        disabled: false,
      });
    }

    buttons.push({
      id: "next",
      caption: "Volgende",
      active: false,
      disabled: currentPage === this.pageCount - 1,
    });

    return buttons;
  }

  renderButtons() {
    const html = this.pagerTemplate({ buttons: this.generateButtonList() });

    this.element.html(html);

    const links = this.element.find(".page-link");
    const that = this;

    links.each(function (ind) {
      let el = $(this);
      let page = el.data("page");
      $(this).on("click", function () {
        if (page === "prev") {
          that.itemPosition -= that.itemsPerPage;
        } else if (page === "next") {
          that.itemPosition += that.itemsPerPage;
        } else {
          that.itemPosition = that.itemsPerPage * +page;
        }
        if (that.itemPosition >= that.content.length)
          that.itemPosition = that.content.length - that.itemsPerPage;
        if (that.itemPosition < 0) that.itemPosition = 0;
        that.render();
      });
    });
  }

  renderContent() {
    const currentPage = Math.floor(this.itemPosition / this.itemsPerPage);

    const start = this.itemPosition;
    const end = start + this.itemsPerPage;

    const contentHtml = this.contentRenderFunc(this.content.slice(start, end));

    this.element.find("#content-viewport").html(contentHtml);
  }

  render() {
    this.renderButtons();
    this.renderContent();
    this.onRender();
  }
}
